// Free distance calculation alternatives

import { validatePostcodeHybrid, FreePostcodeResult } from "./freePostcodeValidator";
import postcodeData from "./postcode.json";

interface FreeDistanceResult {
  success: boolean;
  distance?: {
    kilometers: number;
    miles: number;
    estimatedDrivingTime?: string;
  };
  visitorLocation?: {
    suburb: string;
    state: string;
    postcode: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  error?: string;
}

// Venue location - Moore Park
const VENUE_LOCATION = {
  address: "Level 5 Car Park, 207 Park Rd S, Moore Park NSW 2021",
  coordinates: {
    lat: -33.8986,
    lng: 151.2296,
  },
  name: "Moore Park Venue",
};

// Postcodes that always receive the elevated $25 location discount,
// regardless of the calculated distance from the venue.
const PREMIUM_DISCOUNT_POSTCODES: ReadonlySet<string> = new Set([
  // Parramatta and surrounds
  "2145", "2150", "2151", "2152",
  // Penrith and surrounds
  "2747", "2748", "2749", "2750", "2753", "2759", "2760", "2773", "2774",
]);
const PREMIUM_DISCOUNT_AMOUNT = 25;

// postcode.json is the source of truth for location-discount eligibility: every
// serviceable postcode is listed there with a pre-computed discountPercentage.
const POSTCODE_DISCOUNTS: ReadonlyMap<string, number> = new Map(
  (postcodeData as { postcode: string; discountPercentage?: number }[]).map((entry) => [entry.postcode, entry.discountPercentage ?? 0]),
);

// 46 Greater Sydney postcodes are absent from postcode.json (mostly PO-box-only
// ranges plus a few outer suburbs). They are geocoded on demand, so these ranges
// keep them eligible and they fall through to the distance tiers below.
// Ranges are inclusive on both ends. Ref: HK Active Sprint #304.
const GREATER_SYDNEY_POSTCODE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [2000, 2234],
  [2555, 2574],
  [2745, 2770],
];

/**
 * Returns true if the supplied 4-digit Australian postcode falls within one of
 * the Greater Sydney ranges.
 */
export const isGreaterSydneyPostcode = (postcode: string | undefined): boolean => {
  if (!postcode || !/^\d{4}$/.test(postcode)) return false;
  const numericPostcode = parseInt(postcode, 10);
  return GREATER_SYDNEY_POSTCODE_RANGES.some(([lo, hi]) => numericPostcode >= lo && numericPostcode <= hi);
};

/**
 * Returns true if the supplied postcode is eligible for a location discount —
 * either because it is listed in postcode.json or because it is a Greater
 * Sydney postcode the dataset does not cover.
 */
export const isDiscountEligiblePostcode = (postcode: string | undefined): boolean => {
  if (!postcode || !/^\d{4}$/.test(postcode)) return false;
  return POSTCODE_DISCOUNTS.has(postcode) || isGreaterSydneyPostcode(postcode);
};

/**
 * Calculate the per-line-item location discount (in dollars) based on
 * the visitor's postcode and distance from the venue.
 *
 * Rules:
 *  - Ineligible postcode → $0 (gate, overrides everything below)
 *  - PREMIUM_DISCOUNT_POSTCODES (Parramatta, Penrith) → $25 (overrides the dataset amount)
 *  - Postcode listed in postcode.json → its discountPercentage
 *  - Distance > 20 km → $10
 *  - Distance > 8 km  → $5
 *  - Otherwise        → $0
 */
export const calculateLocationDiscount = (postcode: string | undefined, kilometers: number): number => {
  if (!isDiscountEligiblePostcode(postcode)) {
    return 0;
  }

  if (postcode && PREMIUM_DISCOUNT_POSTCODES.has(postcode)) {
    return PREMIUM_DISCOUNT_AMOUNT;
  }

  const datasetDiscount = postcode ? POSTCODE_DISCOUNTS.get(postcode) : undefined;
  if (datasetDiscount !== undefined) {
    return datasetDiscount;
  }

  if (kilometers > 20) return 10;
  if (kilometers > 8) return 5;
  return 0;
};

/**
 * Calculate straight-line distance using Haversine formula (100% free, no API)
 */
const calculateStraightLineDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Estimate driving time based on distance (rough calculation)
 */
const estimateDrivingTime = (kilometers: number): string => {
  // Simple estimation based on Australian city driving speeds
  let estimatedMinutes: number;

  if (kilometers <= 5) {
    // City/local driving - 20 km/h average (traffic, lights)
    estimatedMinutes = (kilometers / 20) * 60;
  } else if (kilometers <= 20) {
    // Suburban - 30 km/h average
    estimatedMinutes = (kilometers / 30) * 60;
  } else if (kilometers <= 50) {
    // Mixed suburban/highway - 45 km/h average
    estimatedMinutes = (kilometers / 45) * 60;
  } else {
    // Highway - 60 km/h average
    estimatedMinutes = (kilometers / 60) * 60;
  }

  if (estimatedMinutes < 60) {
    return `${Math.round(estimatedMinutes)} mins`;
  } else {
    const hours = Math.floor(estimatedMinutes / 60);
    const mins = Math.round(estimatedMinutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
};

/**
 * Calculate distance from postcode to venue using free services
 * First checks local postcode.json for pre-calculated distance, then falls back to calculation
 */
export const calculateDistanceFromVenueFree = async (postcode: string): Promise<FreeDistanceResult> => {
  // First, check if we have pre-calculated distance data in local JSON
  const localPostcodeData = (postcodeData as any[]).find((item) => item.postcode === postcode);

  // Validate postcode using free services
  const postcodeResult: FreePostcodeResult = await validatePostcodeHybrid(postcode);

  if (!postcodeResult.isValid || !postcodeResult.location) {
    return {
      success: false,
      error: postcodeResult.error || "Invalid postcode",
    };
  }

  try {
    const visitorCoords = postcodeResult.location.coordinates;
    let straightLineKm: number;

    // Use pre-calculated distance if available from local JSON, otherwise calculate it
    if (localPostcodeData && localPostcodeData.distanceInKm) {
      straightLineKm = localPostcodeData.distanceInKm;
    } else {
      // Calculate straight-line distance (free, no API needed)
      straightLineKm = calculateStraightLineDistance(visitorCoords.lat, visitorCoords.lng, VENUE_LOCATION.coordinates.lat, VENUE_LOCATION.coordinates.lng);
    }

    // Estimate driving time (free calculation)
    const estimatedDrivingTime = estimateDrivingTime(straightLineKm);

    return {
      success: true,
      distance: {
        kilometers: Math.round(straightLineKm * 10) / 10,
        miles: Math.round(straightLineKm * 0.621371 * 10) / 10,
        estimatedDrivingTime,
      },
      visitorLocation: {
        suburb: postcodeResult.location.suburb,
        state: postcodeResult.location.state,
        postcode: postcodeResult.location.postcode,
        coordinates: visitorCoords,
      },
    };
  } catch (error) {
    console.error("Distance calculation error:", error);
    return {
      success: false,
      error: "Unable to calculate distance",
    };
  }
};

